import React, { useEffect, useRef, useState } from 'react';
import { FileDown, Printer, Wallet, X } from 'lucide-react';
import useDialog from '../../hooks/useDialog';
import usePdfDownload from '../../hooks/usePdfDownload';
import { brandingAPI, cashDrawerAPI } from '../../services/api';
import { cleanDecimal } from '../invoices/InvoiceEditors';
import { formatPaisa, formatRs, fromPaisa, toPaisa } from '../../utils/money';
import { DrawerSlip, drawerTime, overShortLabel, printDrawerSlip, saveDrawerSlipPdf } from './DrawerSlip';

/**
 * Counts and closes a cash drawer.
 *
 * For the cashier the count is blind: the dialog asks what is in the drawer and shows nothing of
 * what it should hold until the count is in. A count that matches closes the drawer at once. One
 * that does not shows the difference and asks for the reason; the count cannot be changed then,
 * because the server keeps it. Someone closing another cashier's drawer (`supervisor`) sees the
 * expected figure from the start, since it is not their count being checked.
 *
 * Once closed, the closing slip is shown the way a token slip is, with Print slip and Done.
 */
const CloseDrawerDialog = ({ drawer, supervisor = false, onClose, onClosed }) => {
  const [stage, setStage] = useState(drawer?.counted ? 'reason' : 'count');
  const [result, setResult] = useState(null);
  const [typed, setTyped] = useState('');
  const [focused, setFocused] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [printError, setPrintError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [branding, setBranding] = useState(null);
  const countRef = useRef(null);
  const reasonRef = useRef(null);
  const printRef = useRef(null);
  const slipRef = useRef(null);

  const dismiss = () => { if (!saving) onClose(); };
  const dialogRef = useDialog(dismiss, {
    confirmDiscard: false,
    initialFocus: stage === 'count' ? countRef : reasonRef,
  });

  useEffect(() => {
    let live = true;
    brandingAPI.get().then((b) => { if (live) setBranding(b); }).catch(() => {});
    return () => { live = false; };
  }, []);

  // Each step starts where the cashier types next.
  useEffect(() => {
    const target = { count: countRef, reason: reasonRef, closed: printRef }[stage];
    if (target?.current) target.current.focus();
  }, [stage]);

  const figures = result?.drawer || drawer || {};

  const settle = (res) => {
    setResult(res);
    if (res?.stage === 'CLOSED') {
      setStage('closed');
      if (onClosed) onClosed(res);
    } else {
      setStage('reason');
    }
  };

  const submitCount = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (typed === '' || typed === '.') {
      setError('Enter the cash counted in the drawer.');
      countRef.current?.focus();
      return;
    }
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      settle(await cashDrawerAPI.count(drawer.id, fromPaisa(toPaisa(typed))));
    } catch (err) {
      const data = err?.data || err?.body || {};
      if (data.code === 'COUNT_ENTERED' && data.drawer) settle({ ...data, stage: 'COUNTED' });
      else if (data.code === 'DRAWER_CLOSED' && data.drawer) settle({ ...data, stage: 'CLOSED' });
      else setError(err?.message || 'The count could not be saved. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const submitReason = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (!reason.trim()) {
      setError('Say why the drawer is over or short.');
      reasonRef.current?.focus();
      return;
    }
    setSaving(true);
    setError(null);
    try {
      settle(await cashDrawerAPI.close(drawer.id, reason.trim()));
    } catch (err) {
      const data = err?.data || err?.body || {};
      if (data.code === 'DRAWER_CHANGED') {
        // Money moved after the count, so the count no longer stands: count again from scratch.
        setResult(null);
        setTyped('');
        setReason('');
        setNotice(err.message || 'Money went into or out of the drawer after the count. Count it again.');
        setStage('count');
      } else if (data.code === 'DRAWER_CLOSED' && data.drawer) {
        settle({ ...data, stage: 'CLOSED' });
      } else {
        setError(err?.message || 'The drawer could not be closed. Try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  const print = () => {
    setPrintError(null);
    if (!printDrawerSlip(slipRef.current)) {
      setPrintError('Could not open the print window. Please allow pop-ups for this site.');
    }
  };
  const [downloading, download] = usePdfDownload();
  const savePdf = () => download(() => saveDrawerSlipPdf(slipRef.current, result?.drawer));

  const title = stage === 'closed'
    ? 'Drawer closed'
    : supervisor
      ? `Close ${drawer?.cashierName || 'this cashier'}'s drawer`
      : 'Close your cash drawer';

  const difference = toPaisa(figures.overShort);
  const countDisplay = focused || typed === '' ? typed : formatPaisa(toPaisa(typed));
  const secondary = 'flex-1 px-4 py-2.5 border-2 border-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-50 disabled:opacity-60';
  const primary = 'flex-1 px-4 py-2.5 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-700 disabled:opacity-60';

  return (
    <div
      ref={dialogRef}
      aria-labelledby="close-drawer-title"
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4"
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden flex flex-col max-h-[92vh]">
        <div className="bg-gradient-to-r from-navy-800 to-navy-700 px-6 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
              {stage === 'closed'
                ? <Printer className="h-5 w-5 text-white" aria-hidden="true" />
                : <Wallet className="h-5 w-5 text-white" aria-hidden="true" />}
            </div>
            <div className="min-w-0">
              <h2 id="close-drawer-title" className="text-lg font-bold text-white truncate">{title}</h2>
              <p className="text-emerald-100 text-sm truncate">{`Opened ${drawerTime(drawer?.openedAt)}`}</p>
            </div>
          </div>
          <button type="button" aria-label="Close" onClick={dismiss} className="p-2 hover:bg-white/20 rounded-xl">
            <X className="h-5 w-5 text-white" />
          </button>
        </div>

        {stage === 'count' && (
          <form onSubmit={submitCount} noValidate className="flex flex-col min-h-0">
            <div className="p-6 space-y-4 overflow-y-auto">
              {notice && <p role="alert" className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900">{notice}</p>}
              <dl className="space-y-1 text-sm">
                <div className="flex justify-between gap-4">
                  <dt className="text-gray-600">Starting cash</dt>
                  <dd className="font-semibold tabular-nums">{formatRs(drawer?.openingFloat)}</dd>
                </div>
                {supervisor && figures.expectedCash != null && (
                  <div className="flex justify-between gap-4">
                    <dt className="text-gray-600">Should hold now</dt>
                    <dd className="font-semibold tabular-nums">{formatRs(figures.expectedCash)}</dd>
                  </div>
                )}
              </dl>
              <div>
                <label htmlFor="drawer-counted-cash" className="block text-xs font-semibold text-gray-600 mb-1">
                  Cash in the drawer <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center rounded-xl border-2 border-gray-200 px-3 focus-within:border-emerald-500">
                  <span className="text-sm font-semibold text-gray-500" aria-hidden="true">Rs</span>
                  <input
                    id="drawer-counted-cash"
                    ref={countRef}
                    type="text"
                    inputMode="decimal"
                    autoComplete="off"
                    placeholder="0"
                    value={countDisplay}
                    onFocus={() => setFocused(true)}
                    onBlur={() => setFocused(false)}
                    onChange={(e) => {
                      setTyped(cleanDecimal(e.target.value));
                      if (error) setError(null);
                    }}
                    aria-invalid={Boolean(error)}
                    aria-describedby="drawer-count-help"
                    className="w-full px-2 py-2 text-xl font-bold text-gray-900 tabular-nums focus:outline-none"
                  />
                </div>
                <p id="drawer-count-help" className="mt-1 text-xs text-gray-500">
                  {supervisor
                    ? 'Count every note and coin in the drawer.'
                    : 'Count every note and coin in the drawer, the starting cash included. What the drawer should hold is shown once your count is in.'}
                </p>
                {error && <p role="alert" className="mt-1 text-xs text-red-600">{error}</p>}
              </div>
            </div>
            <div className="px-6 pb-6 flex gap-3">
              <button type="button" onClick={dismiss} disabled={saving} className={secondary}>Cancel</button>
              <button type="submit" disabled={saving} className={primary}>{saving ? 'Saving...' : 'Count and close'}</button>
            </div>
          </form>
        )}

        {stage === 'reason' && (
          <form onSubmit={submitReason} noValidate className="flex flex-col min-h-0">
            <div className="p-6 space-y-4 overflow-y-auto">
              <p
                className={`rounded-xl px-4 py-3 text-lg font-bold ${
                  difference < 0 ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-900'
                }`}
              >
                {overShortLabel(figures.overShort)}
              </p>
              <dl className="space-y-1 text-sm">
                <div className="flex justify-between gap-4">
                  <dt className="text-gray-600">Expected cash</dt>
                  <dd className="font-semibold tabular-nums">{formatRs(figures.expectedCash)}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-gray-600">Counted</dt>
                  <dd className="font-semibold tabular-nums">{formatRs(figures.countedCash)}</dd>
                </div>
              </dl>
              <div>
                <label htmlFor="drawer-difference-reason" className="block text-xs font-semibold text-gray-600 mb-1">
                  Reason for the difference <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="drawer-difference-reason"
                  ref={reasonRef}
                  rows={3}
                  maxLength={500}
                  value={reason}
                  onChange={(e) => {
                    setReason(e.target.value);
                    if (error) setError(null);
                  }}
                  aria-invalid={Boolean(error)}
                  aria-describedby="drawer-reason-help"
                  className="w-full rounded-xl border-2 border-gray-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none resize-none"
                />
                <p id="drawer-reason-help" className="mt-1 text-xs text-gray-500">
                  The count stays as entered. Whoever checks the drawers sees this reason beside it.
                </p>
                {error && <p role="alert" className="mt-1 text-xs text-red-600">{error}</p>}
              </div>
            </div>
            <div className="px-6 pb-6 flex gap-3">
              <button type="button" onClick={dismiss} disabled={saving} className={secondary}>Not now</button>
              <button type="submit" disabled={saving} className={primary}>{saving ? 'Closing...' : 'Close drawer'}</button>
            </div>
          </form>
        )}

        {stage === 'closed' && result && (
          <>
            <div className="p-6 overflow-y-auto">
              <div className="bg-gray-50 rounded-2xl p-4 border-2 border-dashed border-gray-200">
                <div ref={slipRef}>
                  <DrawerSlip result={result} branding={branding} />
                </div>
              </div>
              {printError && <p role="alert" className="mt-2 text-sm text-red-600">{printError}</p>}
            </div>
            <div className="px-6 pb-6 flex gap-3">
              <button type="button" onClick={onClose} className={secondary}>Done</button>
              <button type="button" onClick={savePdf} disabled={downloading} className={`${secondary} inline-flex items-center justify-center gap-2 disabled:opacity-50`}>
                <FileDown className="h-5 w-5" aria-hidden="true" />
                <span>{downloading ? 'Making the PDF…' : 'Download PDF'}</span>
              </button>
              <button
                ref={printRef}
                type="button"
                onClick={print}
                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white rounded-xl font-bold hover:from-emerald-700 hover:to-emerald-800 flex items-center justify-center gap-2 shadow-lg"
              >
                <Printer className="h-5 w-5" aria-hidden="true" />
                <span>Print slip</span>
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CloseDrawerDialog;
